import './ApproveListItems.scss';
import React, { useContext, useMemo } from 'react';
import { IDocumentTable } from '@models/Forms/IForms';
import DataTable, { IDataTableColumn, parseColumnSizing } from '@srm/ui/data-table';
import { DocumentRuntimeContext, isDocumentRuntimeReadOnly } from '@/context/DocumentRuntimeContext';
import { ApproveListRenderCell, ApproveListRenderRowActions, ApproveListRowVersion } from './IApproveListModels';

export interface IApproveListItemsProps {
    items: any[];
    table: IDocumentTable;
    renderCell: ApproveListRenderCell;
    rowVersion?: ApproveListRowVersion;
    renderRowActions?: ApproveListRenderRowActions;
    docId?: string;
}

/** Ключ строки и её родителя в листе согласования (плоский список с иерархией). */
const ROW_KEY = '|Код_активити';
const PARENT_KEY = '|Родитель';
/** Корневой уровень: у верхних строк родитель именно такой. */
const ROOT_PARENT = '-1';

/** Плоский список активити → дерево: у строки свои дети лежат рядом по ключу родителя. */
const buildTree = (items: any[]) => {
    const childrenByParent = new Map<string, any[]>();
    items.forEach((item) => {
        const parent = String(item[PARENT_KEY] ?? ROOT_PARENT);
        const siblings = childrenByParent.get(parent);
        if (siblings) siblings.push(item);
        else childrenByParent.set(parent, [item]);
    });
    return {
        roots: childrenByParent.get(ROOT_PARENT) ?? [],
        childrenOf: (row: any) => childrenByParent.get(String(row[ROW_KEY])),
    };
};

const ApproveListItems: React.FC<IApproveListItemsProps> = ({
    items,
    table,
    renderCell,
    rowVersion,
    renderRowActions,
}: IApproveListItemsProps) => {
    const runtime = useContext(DocumentRuntimeContext);
    const interactionsEnabled = !isDocumentRuntimeReadOnly(runtime);

    const tree = useMemo(() => buildTree(items ?? []), [items]);

    const columns = useMemo<IDataTableColumn<any>[]>(() => {
        const configured = [...(table.tableColumn ?? [])]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((column) => ({
                field: column.key,
                header: column.name,
                ...parseColumnSizing(column.width),
                render: (row: any) => renderCell(row, column),
            }));

        if (!interactionsEnabled || !renderRowActions) return configured;

        // «Открыть строку формой» — первой колонкой, как и в старом списке
        return [
            {
                field: '__viewInForm',
                header: '',
                width: 48,
                allowSorting: false,
                allowHiding: false,
                hideHeaderText: true,
                render: (row: any) => renderRowActions(row, items),
            },
            ...configured,
        ];
    }, [table, renderCell, interactionsEnabled, renderRowActions, items]);

    if (!items) return <></>;

    return (
        <DataTable
            columns={columns}
            data={tree.roots}
            getRowId={(row) => String(row[ROW_KEY])}
            getSubRows={tree.childrenOf}
            rowVersion={rowVersion}
            paging={false}
            emptyText="Нет записей"
        />
    );
};

export default ApproveListItems;
